// Error Boundary Component
// src/components/ErrorBoundary.jsx

import React from 'react';
import { FiAlertTriangle, FiRefreshCw, FiHome } from 'react-icons/fi';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error) {
        // Check if this is a chunk load error
        const isChunkError =
            error.name === 'ChunkLoadError' ||
            (error.message && error.message.includes('Loading chunk'));

        return {
            hasError: true,
            isChunkError
        };
    }

    componentDidCatch(error, errorInfo) {
        // Log error to console
        console.error('Error caught by boundary:', error, errorInfo);

        // Update state
        this.setState({
            error,
            errorInfo
        });

        // Send to error tracking service (e.g., Sentry)
        if (window.Sentry) {
            window.Sentry.captureException(error, {
                contexts: {
                    react: {
                        componentStack: errorInfo.componentStack
                    }
                }
            });
        }
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    };

    handleGoHome = () => {
        // Before going home, try to kill service workers to clear any stuck state
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then((registrations) => {
                for (let registration of registrations) registration.unregister();
            });
        }
        window.location.href = '/';
    };

    handleReload = () => {
        // Aggressive reload for chunk errors
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then((registrations) => {
                for (let registration of registrations) registration.unregister();
                window.location.reload(true);
            });
        } else {
            window.location.reload(true);
        }
    };

    render() {
        if (this.state.hasError) {
            const isDevelopment = process.env.NODE_ENV === 'development';

            return (
                <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
                    <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8">
                        {/* Icon */}
                        <div className="flex justify-center mb-6">
                            <div className="bg-red-100 p-6 rounded-full">
                                <FiAlertTriangle className="text-red-600 text-6xl" />
                            </div>
                        </div>

                        {/* Title */}
                        <h1 className="text-3xl font-black text-gray-900 text-center mb-4">
                            {this.state.isChunkError
                                ? 'يحتاج التطبيق إلى تحديث'
                                : 'عذراً، حدث خطأ غير متوقع'}
                        </h1>

                        {/* Description */}
                        <p className="text-gray-600 text-center mb-8">
                            {this.state.isChunkError
                                ? 'تم تحديث أجزاء من التطبيق. يرجى الضغط على الزر أدناه لإعادة تحميل النسخة الجديدة.'
                                : 'نعتذر عن هذا الإزعاج. حدث خطأ أثناء معالجة طلبك. يرجى المحاولة مرة أخرى أو الاتصال بالدعم الفني إذا استمرت المشكلة.'}
                        </p>

                        {/* Error Details (Development Only) */}
                        {isDevelopment && this.state.error && !this.state.isChunkError && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                                <h3 className="font-bold text-red-800 mb-2">
                                    تفاصيل الخطأ (Development Mode):
                                </h3>
                                <pre className="text-sm text-red-700 overflow-auto max-h-40 whitespace-pre-wrap">
                                    {this.state.error.toString()}
                                </pre>
                                {this.state.errorInfo && (
                                    <details className="mt-4">
                                        <summary className="cursor-pointer text-red-800 font-semibold">
                                            Component Stack
                                        </summary>
                                        <pre className="text-xs text-red-600 mt-2 overflow-auto max-h-40">
                                            {this.state.errorInfo.componentStack}
                                        </pre>
                                    </details>
                                )}
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={this.handleReload}
                                className="flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-bold shadow-md"
                            >
                                <FiRefreshCw className="animate-spin-slow" />
                                <span>{this.state.isChunkError ? 'تحديث التطبيق الآن' : 'حاول مرة أخرى'}</span>
                            </button>

                            <button
                                onClick={this.handleGoHome}
                                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-bold"
                            >
                                <FiHome />
                                <span>العودة للرئيسية</span>
                            </button>
                        </div>

                        {/* Support Info */}
                        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                            <p className="text-sm text-gray-500">
                                إذا استمرت المشكلة، يرجى الاتصال بالدعم الفني:
                            </p>
                            <p className="text-sm text-emerald-600 font-bold mt-2">
                                support@dfoodpro.tech
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;

// Error Fallback Component (for React Query / SWR)
export const ErrorFallback = ({ error, resetErrorBoundary }) => {
    return (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <FiAlertTriangle className="text-red-600 text-4xl mx-auto mb-4" />
            <h3 className="text-xl font-bold text-red-800 mb-2">حدث خطأ</h3>
            <p className="text-red-600 mb-4">{error.message}</p>
            <button
                onClick={resetErrorBoundary}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
                حاول مرة أخرى
            </button>
        </div>
    );
};

// 404 Not Found Component
export const NotFoundPage = () => {
    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
            <div className="max-w-md w-full text-center">
                <div className="text-9xl font-black text-emerald-600 mb-4">404</div>
                <h1 className="text-3xl font-black text-gray-900 mb-4">الصفحة غير موجودة</h1>
                <p className="text-gray-600 mb-8">
                    عذراً، الصفحة التي تبحث عنها غير موجودة أو تم نقلها.
                </p>
                <button
                    onClick={() => (window.location.href = '/')}
                    className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-bold"
                >
                    العودة للرئيسية
                </button>
            </div>
        </div>
    );
};

// Network Error Component
export const NetworkError = ({ onRetry }) => {
    return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
            <div className="text-yellow-600 text-4xl mb-4">📡</div>
            <h3 className="text-xl font-bold text-yellow-800 mb-2">مشكلة في الاتصال</h3>
            <p className="text-yellow-600 mb-4">
                يبدو أن هناك مشكلة في الاتصال بالإنترنت. يرجى التحقق من اتصالك والمحاولة مرة أخرى.
            </p>
            <button
                onClick={onRetry}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
            >
                إعادة المحاولة
            </button>
        </div>
    );
};

// Empty State Component
export const EmptyState = ({
    icon = '📦',
    title = 'لا توجد بيانات',
    description = 'لم يتم العثور على أي بيانات',
    action = null
}) => {
    return (
        <div className="bg-gray-50 rounded-xl p-12 text-center">
            <div className="text-6xl mb-4">{icon}</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{title}</h3>
            <p className="text-gray-600 mb-6">{description}</p>
            {action && action}
        </div>
    );
};
